import { Moment } from 'moment'

import { ConfigFilterItem, MultipleDefaultFile } from 'Core/interfaces'

export enum ServiceLocationFilterFields {
	serviceLocationId = 'serviceLocationId',
	billingLocationId = 'billingLocationId',
	programManagerId = 'programManagerId'
}

export interface ProgramManagerFilter {
	id: number
	firstName: BasicString
	lastName: BasicString
}

export enum ContactFieldNames {
	firstName = 'firstName',
	lastName = 'lastName',
	email = 'email',
	jobTitle = 'jobTitle',
	office = 'office',
	mobile = 'mobile',
	isActive = 'isActive',
	isMainContact = 'isMainContact',
	isInvoiceContact = 'isInvoiceContact',
	isManifestContact = 'isManifestContact',
	isSurvey = 'isSurvey',
	isOrderingContact = 'isOrderingContact',
	isCollectionContact = 'isCollectionContact',
	isOffspecContact = 'isOffspecContact'
}

export enum CurrentServiceProductStateFields {
	id = 'id',
	name = 'name',
	annualVolume = 'annualVolume',
	unit = 'unit',
	annualHaulsOrServices = 'annualHaulsOrServices',
	disposalMethod = 'disposalMethod',
	currentVendor = 'currentVendor',
	contactExpiration = 'contactExpiration',
	containerType = 'containerType',
	containerSize = 'containerSize'
}

export enum CurrentServiceProductStateFormFields {
	name = 'name',
	currentVendor = 'currentVendor',
	containerType = 'containerType',
	containerSize = 'containerSize',
	annualVolume = 'annualVolume',
	annualHaulsOrServices = 'annualHaulsOrServices',
	unitOfMeasureId = 'unitOfMeasureId',
	treatmentMethodId = 'treatmentMethodId',
	contactExpiration = 'contactExpiration'
}

export enum CurrentSupplierContactFormFields {
	currentVendor = 'currentVendor',
	vendorContactName = 'vendorContactName',
	vendorContactEmail = 'vendorContactEmail',
	vendorContactPhone = 'vendorContactPhone',
	existingPaymentTerms = 'existingPaymentTerms',
	wastesMaterialsServices = 'wastesMaterialsServices',
	isUnderContact = 'isUnderContact'
}

export enum HistoricalInvoiceFormFields {
	files = 'files'
}

export declare namespace ServiceLocation {
	interface BillingLocation extends Entity {
		billingAgencyId: number
	}
	interface Address {
		id: number
		addressLine1: BasicString
		addressLine2: BasicString
		city: BasicString
		zipCode: BasicString
		stateProvince: CodeEntity | null
		county: Entity | null
		country: CodeEntity | null
	}

	interface ProgramManager {
		id: number
		firstName: BasicString
		lastName: BasicString
		email: BasicString
		mobilePhone: BasicString
		officePhone: BasicString
	}

	interface Basic extends Entity {
		billingLocation: BillingLocation
		address: Nullable<Address>
		programManager: ProgramManager
		serviceTeam: Entity
	}

	namespace Filter {
		interface Config {
			[ServiceLocationFilterFields.serviceLocationId]: ConfigFilterItem<string, string>
			[ServiceLocationFilterFields.billingLocationId]: ConfigFilterItem<string, string>
			[ServiceLocationFilterFields.programManagerId]: ConfigFilterItem<string, string>
		}
	}
	interface Detailed extends Entity {
		billingLocation: BillingLocation | null
		address: Address
		programManager: ProgramManager | null
		serviceTeam: Entity
	}
	namespace Contact {
		interface Basic {
			id: number
			contactId: BasicNumber
			contactName: BasicString
			officePhone: BasicString
			mobilePhone: BasicString
			email: BasicString
			jobTitle: BasicString
			isActive: boolean
			isMainContact: boolean
			isInvoiceContact: boolean
			isManifestContact: boolean
			isOrderingContact: boolean
			isSurvey: boolean
			isCollectionContact: boolean
			isOffspecContact: boolean
		}

		interface Detailed {
			id: number
			contactId: BasicNumber
			contactName: BasicString
			contactFirstName: BasicString
			contactLastName: BasicString
			contactMiddleInitial: BasicString
			officePhone: BasicString
			mobilePhone: BasicString
			email: BasicString
			jobTitle: BasicString
			isActive: boolean
			isMainContact: boolean
			isInvoiceContact: boolean
			isManifestContact: boolean
			isOrderingContact: boolean
			isSurvey: boolean
			isCollectionContact: boolean
			isOffspecContact: boolean
		}
		namespace Generic {
			interface Form {
				[ContactFieldNames.firstName]: BasicString
				[ContactFieldNames.lastName]: BasicString
				[ContactFieldNames.email]: BasicString
				[ContactFieldNames.jobTitle]: BasicString
				[ContactFieldNames.office]: BasicString
				[ContactFieldNames.mobile]: BasicString
				[ContactFieldNames.isActive]: boolean
				[ContactFieldNames.isMainContact]: boolean
				[ContactFieldNames.isInvoiceContact]: boolean
				[ContactFieldNames.isManifestContact]: boolean
				[ContactFieldNames.isSurvey]: boolean
				[ContactFieldNames.isOrderingContact]: boolean
				[ContactFieldNames.isCollectionContact]: boolean
				[ContactFieldNames.isOffspecContact]: boolean
			}
			interface Request {
				firstName: BasicString
				lastName: BasicString
				email: BasicString
				jobTitle: BasicString
				office: BasicString
				mobile: BasicString
				isActive: boolean
				isMainContact: boolean
				isInvoiceContact: boolean
				isManifestContact: boolean
				isSurvey: boolean
				isOrderingContact: boolean
				isCollectionContact: boolean
				isOffspecContact: boolean
				billingAgencyIds: number[]
			}
		}
	}

	namespace Service {
		interface Basic {
			[CurrentServiceProductStateFields.id]: number
			[CurrentServiceProductStateFields.name]: BasicString
			[CurrentServiceProductStateFields.annualVolume]: BasicNumber
			[CurrentServiceProductStateFields.unit]: Entity<number>
			[CurrentServiceProductStateFields.annualHaulsOrServices]: BasicNumber
			[CurrentServiceProductStateFields.disposalMethod]: Entity<number>
			[CurrentServiceProductStateFields.currentVendor]: BasicString
			[CurrentServiceProductStateFields.contactExpiration]: BasicString
			[CurrentServiceProductStateFields.containerSize]: BasicString
			[CurrentServiceProductStateFields.containerType]: BasicString
		}

		interface Form {
			[CurrentServiceProductStateFormFields.name]: BasicString
			[CurrentServiceProductStateFormFields.currentVendor]: BasicString
			[CurrentServiceProductStateFormFields.containerType]: BasicString
			[CurrentServiceProductStateFormFields.containerSize]: BasicString
			[CurrentServiceProductStateFormFields.annualVolume]: BasicNumber
			[CurrentServiceProductStateFormFields.annualHaulsOrServices]: BasicNumber
			[CurrentServiceProductStateFormFields.unitOfMeasureId]: BasicNumber
			[CurrentServiceProductStateFormFields.treatmentMethodId]: BasicNumber
			[CurrentServiceProductStateFormFields.contactExpiration]: Nullable<Moment>
		}

		interface Request {
			[CurrentServiceProductStateFormFields.name]: BasicString
			[CurrentServiceProductStateFormFields.currentVendor]: BasicString
			[CurrentServiceProductStateFormFields.containerType]: BasicString
			[CurrentServiceProductStateFormFields.containerSize]: BasicString
			[CurrentServiceProductStateFormFields.annualVolume]: BasicNumber
			[CurrentServiceProductStateFormFields.annualHaulsOrServices]: BasicNumber
			[CurrentServiceProductStateFormFields.unitOfMeasureId]: BasicNumber
			[CurrentServiceProductStateFormFields.treatmentMethodId]: BasicNumber
			[CurrentServiceProductStateFormFields.contactExpiration]: BasicString
		}
	}

	namespace SupplierContact {
		interface Basic {
			id: number
			currentVendor: BasicString
			vendorContactName: BasicString
			vendorContactEmail: BasicString
			vendorContactPhone: BasicString
			wastesMaterialsServices: BasicString
		}

		interface Form {
			[CurrentSupplierContactFormFields.currentVendor]: BasicString
			[CurrentSupplierContactFormFields.vendorContactName]: BasicString
			[CurrentSupplierContactFormFields.vendorContactEmail]: BasicString
			[CurrentSupplierContactFormFields.vendorContactPhone]: BasicString
			[CurrentSupplierContactFormFields.existingPaymentTerms]: BasicString
			[CurrentSupplierContactFormFields.wastesMaterialsServices]: BasicString
			[CurrentSupplierContactFormFields.isUnderContact]: boolean
		}

		interface Detailed {
			id: number
			currentVendor: BasicString
			vendorContactName: BasicString
			vendorContactEmail: BasicString
			vendorContactPhone: BasicString
			wastesMaterialsServices: BasicString
			existingPaymentTerms: BasicString
			isUnderContact: boolean
		}
	}
	namespace HistoricalInvoice {
		interface Basic {
			id: number
			documentName: BasicString
			uploadDate: BasicString
		}

		interface Form {
			[HistoricalInvoiceFormFields.files]: Nullable<MultipleDefaultFile>
		}

		interface Request {
			Files: File[]
		}
	}
}
